import React from 'react';
import { TrendingUp, TrendingDown, Target, ShieldAlert, Crown, CalendarClock } from 'lucide-react';
import type { Signal } from '../../features/signals/api/signals';

function createdText(iso?: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('id-ID', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' });
  } catch {
    return iso;
  }
}

function tvImageUrl(link?: string): string {
  if (!link) return '';
  const u = link.trim();
  // Direct image (png/jpg/webp) → use as-is
  if (/\.(png|jpe?g|webp|gif)(\?|$)/i.test(u) || /s3\.tradingview\.com\/snapshots/i.test(u)) return u;
  // TradingView share link: https://www.tradingview.com/x/{ID}/ → snapshot image
  const m = u.match(/tradingview\.com\/x\/([A-Za-z0-9]+)/);
  if (m) return `https://s3.tradingview.com/snapshots/${m[1][0].toLowerCase()}/${m[1]}.png`;
  return u;
}

export function SignalCard({ s, children }: { s: Signal; children?: React.ReactNode }) {
  const isBuy = s.direction === 'buy';
  const img = s.image_url || tvImageUrl(s.image_link) || `${process.env.REACT_APP_BACKEND_URL}/api/v1/public/signals/${s.id}/card.png`;
  return (
    <div data-testid="signal-card" className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
      {img && (
        <img src={img} alt={s.instrument} className="w-full max-h-72 object-cover border-b border-white/10" />
      )}
      <div className="p-5">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="text-lg font-medium text-white tracking-tight">{s.instrument}</span>
          <span className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${isBuy ? 'bg-emerald-400/15 text-emerald-300 border border-emerald-400/30' : 'bg-red-400/15 text-red-300 border border-red-400/30'}`}>
            {isBuy ? <TrendingUp className="w-3.5 h-3.5" strokeWidth={2} /> : <TrendingDown className="w-3.5 h-3.5" strokeWidth={2} />}
            {isBuy ? 'BUY' : 'SELL'}
          </span>
          {s.audience === 'vip' && (
            <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300 bg-amber-400/15 border border-amber-400/30 px-2 py-0.5 rounded-full"><Crown className="w-3 h-3" strokeWidth={1.5} /> VIP</span>
          )}
        </div>
        {s.created_at && (
          <p className="flex items-center gap-1.5 text-xs text-white/40 mb-3" data-testid="signal-created-at">
            <CalendarClock className="w-3.5 h-3.5 text-cyan-400/70" strokeWidth={1.5} /> {createdText(s.created_at)} WIB
          </p>
        )}
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3">
            <p className="text-[10px] uppercase tracking-wider text-white/40">Entry</p>
            <p className="text-white font-medium mt-1">{s.entry_price}</p>
          </div>
          <div className="bg-white/[0.03] border border-emerald-400/20 rounded-xl p-3">
            <p className="text-[10px] uppercase tracking-wider text-emerald-400/70 flex items-center justify-center gap-1"><Target className="w-3 h-3" strokeWidth={1.5} /> TP</p>
            <p className="text-emerald-300 font-medium mt-1">{s.take_profit ?? '—'}</p>
          </div>
          <div className="bg-white/[0.03] border border-red-400/20 rounded-xl p-3">
            <p className="text-[10px] uppercase tracking-wider text-red-400/70 flex items-center justify-center gap-1"><ShieldAlert className="w-3 h-3" strokeWidth={1.5} /> SL</p>
            <p className="text-red-300 font-medium mt-1">{s.stop_loss ?? '—'}</p>
          </div>
        </div>
        {s.note && <p className="text-sm text-white/60 mt-4 whitespace-pre-line">{s.note}</p>}
        {children}
      </div>
    </div>
  );
}
